import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { TrendUp, Warning, CurrencyEur, Users, Target, Check, X, PencilSimple } from '@phosphor-icons/react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useRequireAdmin } from '@/hooks/useRequireAdmin';
import { fmtMonth } from '@/utils/dateFmt';

const API = process.env.REACT_APP_BACKEND_URL;

export default function DesempenhoPage() {
  useRequireAdmin();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const isViewer = user?.role === 'viewer';
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [perf, setPerf] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [nutris, setNutris] = useState([]);
  const [selectedNutri, setSelectedNutri] = useState('');
  const [showGoal, setShowGoal] = useState(false);
  const [goalTarget, setGoalTarget] = useState('');
  const [showPricing, setShowPricing] = useState(false);
  const [pricingForm, setPricingForm] = useState({ mensal: '', trimestral: '', semestral: '', commission_pct: '60' });
  const [showClosure, setShowClosure] = useState(false);
  const [closureForm, setClosureForm] = useState({ transfer_amount: '', transferred: false, invoice_emitted: false });
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const params = `?month=${month}${selectedNutri ? `&nutri_id=${selectedNutri}` : ''}`;
      const [pRes, aRes] = await Promise.all([
        axios.get(`${API}/api/nutri-performance${params}`),
        axios.get(`${API}/api/performance-alerts`),
      ]);
      setPerf(pRes.data);
      setAlerts(aRes.data || []);
    } catch {} finally { setLoading(false); }
  }, [month, selectedNutri]);

  useEffect(() => {
    if (isAdmin || isViewer) {
      (async () => { try { const { data } = await axios.get(`${API}/api/staff`); setNutris(data); if (isViewer && data.length) { const firstNutri = data.find(n => n.user_id !== user?.user_id) || data[0]; setSelectedNutri(firstNutri.user_id); } } catch {} })();
    }
  }, [isAdmin, isViewer, user]);
  useEffect(() => { fetchData(); }, [fetchData]);

  const saveGoal = async () => {
    try {
      await axios.put(`${API}/api/nutri-goals`, { month, target: parseInt(goalTarget), nutri_id: selectedNutri || user?.user_id });
      toast.success('Meta guardada'); setShowGoal(false); fetchData();
    } catch { toast.error('Erro'); }
  };

  const savePricing = async () => {
    const nid = selectedNutri || user?.user_id;
    try {
      await axios.put(`${API}/api/nutri-pricing/${nid}`, { prices: { mensal: parseFloat(pricingForm.mensal) || 0, trimestral: parseFloat(pricingForm.trimestral) || 0, semestral: parseFloat(pricingForm.semestral) || 0 }, commission_pct: parseFloat(pricingForm.commission_pct) || 60 });
      toast.success('Preços guardados'); setShowPricing(false); fetchData();
    } catch { toast.error('Erro'); }
  };

  const saveClosure = async () => {
    try {
      await axios.put(`${API}/api/month-closure`, { nutri_id: selectedNutri || user?.user_id, month, ...closureForm, transfer_amount: parseFloat(closureForm.transfer_amount) || 0 });
      toast.success('Fecho atualizado'); setShowClosure(false); fetchData();
    } catch { toast.error('Erro'); }
  };

  if (loading) return <div className="text-center py-12 text-brand-muted">A carregar...</div>;

  const c = perf?.consultations || {};
  const g = perf?.goal || {};
  const f = perf?.finance || {};
  const cl = perf?.closure || {};

  return (
    <div className="fade-in" data-testid="desempenho-page">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <h2 className="font-heading font-medium text-xl text-brand-text">Desempenho</h2>
          <p className="text-xs text-brand-muted mt-0.5">Métricas mensais e financeiro</p>
        </div>
        <div className="flex gap-2 items-center">
          {(isAdmin || isViewer) && nutris.length > 0 && (
            <select value={selectedNutri} onChange={e => setSelectedNutri(e.target.value)} className="border border-brand-border rounded-xl px-3 py-2 text-xs bg-white" data-testid="nutri-selector">
              {!isViewer && <option value="">Eu (admin)</option>}
              {nutris.filter(n => isViewer || n.user_id !== user?.user_id).map(n => <option key={n.user_id} value={n.user_id}>{n.name}</option>)}
            </select>
          )}
          <Input type="month" value={month} onChange={e => setMonth(e.target.value)} className="rounded-xl border-brand-border text-sm w-auto" data-testid="perf-month" />
        </div>
      </div>

      {/* Alerts */}
      {alerts.length > 0 && (
        <div className="space-y-2 mb-5">
          {alerts.map((a, i) => (
            <div key={i} className={`flex items-center gap-2 p-3 rounded-2xl text-xs ${a.priority === 'high' ? 'bg-red-50 border border-red-200 text-red-700' : 'bg-amber-50 border border-amber-200 text-amber-700'}`} data-testid={`alert-${a.type}`}>
              <Warning size={16} weight="fill" />
              <span className="flex-1">
                {a.type === 'transfer_overdue' && `Transferência pendente para ${a.nutri} (${a.month})`}
                {a.type === 'invoice_missing' && `Fatura não emitida por ${a.nutri} (${a.month})`}
                {a.type === 'goal_at_risk' && `Meta em risco: ${a.nutri} com ${a.achieved}/${a.target} consultas a meio do mês`}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <div className="bg-white border border-brand-border rounded-2xl p-4 text-center">
          <p className="text-2xl font-heading font-semibold text-brand-green">{c.total || 0}</p>
          <p className="text-[10px] text-brand-muted">Consultas realizadas</p>
        </div>
        <div className="bg-white border border-brand-border rounded-2xl p-4 text-center">
          <p className="text-2xl font-heading font-semibold text-brand-text">{g.pct || 0}%</p>
          <p className="text-[10px] text-brand-muted">Meta ({g.achieved || 0}/{g.target || '-'})</p>
        </div>
        <div className="bg-white border border-brand-border rounded-2xl p-4 text-center">
          <p className="text-2xl font-heading font-semibold text-brand-green">{f.nutri_earnings || 0}€</p>
          <p className="text-[10px] text-brand-muted">Ganhos ({f.commission_pct}%)</p>
        </div>
        <div className="bg-white border border-brand-border rounded-2xl p-4 text-center">
          <p className="text-2xl font-heading font-semibold text-red-500">{(c.cancelled || 0) + (c.no_show || 0)}</p>
          <p className="text-[10px] text-brand-muted">Cancelamentos/faltas</p>
        </div>
      </div>

      {/* Dias trabalhados reais */}
      {perf?.productivity && (
        <div className="bg-white border border-brand-border rounded-2xl p-4 mb-4 flex items-center gap-6" data-testid="dias-trabalhados">
          <div>
            <p className="text-2xl font-heading font-semibold text-brand-green">{perf.productivity.days_worked || 0}</p>
            <p className="text-[10px] text-brand-muted">dias trabalhados em {fmtMonth(month)}</p>
          </div>
          <div>
            <p className="text-2xl font-heading font-semibold text-brand-text">{perf.productivity.consults_per_day || 0}</p>
            <p className="text-[10px] text-brand-muted">consultas/dia</p>
          </div>
        </div>
      )}

      {/* Consultas por duração do programa */}
      {Object.keys(c.by_duration || {}).length > 0 && (
        <div className="bg-white border border-brand-border rounded-2xl p-4 mb-4" data-testid="consultas-por-duracao">
          <h3 className="font-heading font-medium text-xs text-brand-muted uppercase tracking-wider mb-2">Consultas por duração do programa</h3>
          <div className="flex gap-3 flex-wrap">
            {['Mensal', 'Trimestral', 'Semestral', 'Sem programa'].filter(t => c.by_duration[t]).map(t => {
              const count = c.by_duration[t];
              const pct = c.total ? Math.round((count / c.total) * 100) : 0;
              return (
                <div key={t} className="bg-brand-bg rounded-xl px-4 py-2 text-center min-w-[88px]" data-testid={`duracao-${t.toLowerCase().replace(/\s+/g, '-')}`}>
                  <p className="text-sm font-semibold text-brand-text">{count} <span className="text-[10px] font-normal text-brand-muted">({pct}%)</span></p>
                  <p className="text-[9px] text-brand-muted">{t}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Finance detail */}
      <div className="bg-white border border-brand-border rounded-2xl p-4 mb-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading font-medium text-xs text-brand-muted uppercase tracking-wider">Financeiro</h3>
          <div className="flex gap-1">
            {!isViewer && <button onClick={() => { setShowPricing(true); const nid = selectedNutri || user?.user_id; (async () => { try { const { data } = await axios.get(`${API}/api/nutri-pricing/${nid}`); setPricingForm({ mensal: data.prices?.mensal || '', trimestral: data.prices?.trimestral || '', semestral: data.prices?.semestral || '', commission_pct: data.commission_pct || '60' }); } catch {} })(); }} className="text-[10px] text-brand-green hover:underline" data-testid="edit-pricing">Editar preços</button>}
            {!isViewer && <span className="text-brand-border">|</span>}
            {!isViewer && <button onClick={() => { setGoalTarget(g.target || ''); setShowGoal(true); }} className="text-[10px] text-brand-green hover:underline" data-testid="edit-goal">Editar meta</button>}
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3 text-center">
          <div><p className="text-sm font-semibold text-brand-text">{f.total_revenue || 0}€</p><p className="text-[9px] text-brand-muted">Receita total</p></div>
          <div><p className="text-sm font-semibold text-brand-green">{f.nutri_earnings || 0}€</p><p className="text-[9px] text-brand-muted">Nutri ({f.commission_pct}%)</p></div>
          <div><p className="text-sm font-semibold text-brand-muted">{f.clinic_earnings || 0}€</p><p className="text-[9px] text-brand-muted">Clínica</p></div>
        </div>
        <div className="grid grid-cols-2 gap-3 text-center mt-3 pt-3 border-t border-brand-border">
          <div data-testid="desempenho-recebido"><p className="text-sm font-semibold text-brand-green">{f.received_revenue ?? 0}€</p><p className="text-[9px] text-brand-muted">Recebido</p></div>
          <div data-testid="desempenho-por-cobrar"><p className="text-sm font-semibold text-amber-600">{f.pending_revenue ?? 0}€</p><p className="text-[9px] text-brand-muted">Por cobrar</p></div>
        </div>
      </div>

      {/* Month closure (admin) */}
      {isAdmin && (
        <div className="bg-white border border-brand-border rounded-2xl p-4 mb-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading font-medium text-xs text-brand-muted uppercase tracking-wider">Fecho do mês</h3>
            <button onClick={() => { setClosureForm({ transfer_amount: cl?.transfer_amount || '', transferred: cl?.transferred || false, invoice_emitted: cl?.invoice_emitted || false }); setShowClosure(true); }} className="text-[10px] text-brand-green hover:underline" data-testid="edit-closure">Editar</button>
          </div>
          <div className="flex gap-4 text-xs">
            <div className="flex items-center gap-1">{cl?.transferred ? <Check size={14} className="text-green-600" /> : <X size={14} className="text-red-400" />}<span>Transferência {cl?.transferred ? 'feita' : 'pendente'}</span></div>
            <div className="flex items-center gap-1">{cl?.invoice_emitted ? <Check size={14} className="text-green-600" /> : <X size={14} className="text-red-400" />}<span>Fatura {cl?.invoice_emitted ? 'emitida' : 'pendente'}</span></div>
            {cl?.transfer_amount > 0 && <span className="text-brand-green font-medium">{cl.transfer_amount}€</span>}
          </div>
        </div>
      )}

      {/* Dialogs */}
      <Dialog open={showGoal} onOpenChange={setShowGoal}>
        <DialogContent className="rounded-3xl border-brand-border">
          <DialogHeader><DialogTitle className="font-heading text-brand-text">Meta de consultas</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs text-brand-muted mb-1 block">Consultas previstas ({fmtMonth(month)})</Label><Input type="number" value={goalTarget} onChange={e => setGoalTarget(e.target.value)} className="rounded-xl border-brand-border" data-testid="goal-input" /></div>
            <button onClick={saveGoal} className="w-full bg-brand-green text-white rounded-full py-3 font-medium">Guardar</button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={showPricing} onOpenChange={setShowPricing}>
        <DialogContent className="rounded-3xl border-brand-border">
          <DialogHeader><DialogTitle className="font-heading text-brand-text">Tabela de preços</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-2">
              <div><Label className="text-[9px] text-brand-muted mb-0.5 block">Mensal (€)</Label><Input type="number" value={pricingForm.mensal} onChange={e => setPricingForm({ ...pricingForm, mensal: e.target.value })} className="rounded-lg border-brand-border text-sm h-9" /></div>
              <div><Label className="text-[9px] text-brand-muted mb-0.5 block">Trimestral (€)</Label><Input type="number" value={pricingForm.trimestral} onChange={e => setPricingForm({ ...pricingForm, trimestral: e.target.value })} className="rounded-lg border-brand-border text-sm h-9" /></div>
              <div><Label className="text-[9px] text-brand-muted mb-0.5 block">Semestral (€)</Label><Input type="number" value={pricingForm.semestral} onChange={e => setPricingForm({ ...pricingForm, semestral: e.target.value })} className="rounded-lg border-brand-border text-sm h-9" /></div>
            </div>
            <div><Label className="text-xs text-brand-muted mb-1 block">Comissão da nutri (%)</Label><Input type="number" value={pricingForm.commission_pct} onChange={e => setPricingForm({ ...pricingForm, commission_pct: e.target.value })} className="rounded-xl border-brand-border" /></div>
            <button onClick={savePricing} className="w-full bg-brand-green text-white rounded-full py-3 font-medium">Guardar</button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={showClosure} onOpenChange={setShowClosure}>
        <DialogContent className="rounded-3xl border-brand-border">
          <DialogHeader><DialogTitle className="font-heading text-brand-text">Fecho do mês</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs text-brand-muted mb-1 block">Valor a transferir (€)</Label><Input type="number" value={closureForm.transfer_amount} onChange={e => setClosureForm({ ...closureForm, transfer_amount: e.target.value })} className="rounded-xl border-brand-border" /></div>
            <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={closureForm.transferred} onChange={e => setClosureForm({ ...closureForm, transferred: e.target.checked })} className="rounded" /><span className="text-xs text-brand-muted">Transferência efectuada</span></label>
            <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={closureForm.invoice_emitted} onChange={e => setClosureForm({ ...closureForm, invoice_emitted: e.target.checked })} className="rounded" /><span className="text-xs text-brand-muted">Fatura emitida pela nutri</span></label>
            <button onClick={saveClosure} className="w-full bg-brand-green text-white rounded-full py-3 font-medium">Guardar</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
